<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { CloudApiError, downloadCloudFile, enterpriseProfileApi, marketAnalysisApi, subscribeToCloudEvents } from '@/api/index.js'
import {
  marketAnalysisFailure,
  marketAnalysisRecovery,
  runStatusLabel,
  reportDeliveryLabel,
  stageStatusLabel,
} from '@/presentation/marketAnalysis.js'
import { authStore } from '@/stores/auth.js'
import ReportChapter from '@/components/ReportChapter.vue'
import ReportEvidence from '@/components/ReportEvidence.vue'
import { coalescedRefresh } from '@/api/coalescedRefresh.js'
import { readState, recoveringRead } from '@/api/recoveringRead.js'
import { useProjectRefresh } from '@/composables/useProjectRefresh.js'
import { stageActivityLabel } from '@/presentation/projectBrain.js'
import { analysisSubmission } from '@/api/analysisSubmission.js'
import { projectScope } from '@/utils/projectRefresh.js'

const tenantId = computed(() => authStore.state.selectedTenant?.id || '')
const project = computed(() => authStore.state.assignment?.project || null)
const context = { tenantId: tenantId.value, projectId: project.value?.id }
const profile = ref(null)
const run = ref(null)
const history = ref([])
const readStatus = reactive({ profile: readState(), run: readState(), history: readState() })
const loading = computed(() => !readStatus.run.loaded && !readStatus.run.error)
const readLabels = { profile: '企业资料', run: '分析状态', history: '历史报告' }
const readMessages = computed(() => Object.entries(readStatus).filter(([, state]) => state.error).map(([key, state]) =>
  state.nextAttemptAt === Infinity ? `${readLabels[key]}暂时无法读取，请确认当前账号的访问权限。`
    : `${readLabels[key]}连接暂时中断，正在自动恢复读取。已经显示的内容会保留，无需重新分析。`))
const starting = ref(false)
const submissionPhase = ref('idle')
let mutationGeneration = 0
const stopping = ref(false)
const connectionState = ref('idle')
const errorMessage = ref('')
const statusMessage = ref('')
const downloadMessage = ref('')
const downloadingReportId = ref('')
let stream = null
let pollTimer = null
let startKey = ''
let refreshTimer = null
let disposed = false

const stageLabels = {
  profile_freeze: '冻结企业资料版本', public_web_research: '公开网站与二级网页研究',
  xiaohongshu_research: '小红书公开内容研究', douyin_research: '抖音公开内容研究',
  tiktok_research: 'TikTok 公开内容研究', evidence_freeze: '冻结四通道证据',
  report_generation: '生成 12 章分析报告', report_persist: '保存企业空间报告', pdf_render: '生成报告 PDF',
}
const active = computed(() => ['queued', 'running', 'waiting_retry'].includes(run.value?.status))
const profileNeedsCorrection = computed(() => run.value?.stages?.some(stage => stage.error_code === 'RESEARCH_PROFILE_INVALID'))
const correctedVersionReady = computed(() => profile.value?.current?.id && profile.value.current.id !== run.value?.profile_version_id)
const recovery = computed(() => marketAnalysisRecovery(run.value))
const failureMessage = computed(() => marketAnalysisFailure(run.value))
const activeStage = computed(() => (run.value?.stages || []).find((stage) => stage.stage_key === run.value?.current_stage) || null)
const waitingStage = computed(() => (run.value?.stages || []).find(stage => stage.status === 'waiting_retry'))
const progressSummary = computed(() => run.value?.progress_summary || {})
const attentionItems = computed(() => Array.isArray(run.value?.attention_items) ? run.value.attention_items : [])
const waitingForServiceRecovery = computed(() => run.value?.status === 'failed' && attentionItems.value.length > 0 && !recovery.value.canResume)
const sourceLabels = Object.freeze({ xiaohongshu: '小红书', douyin: '抖音', tiktok: 'TikTok' })
const activeStageLabel = computed(() => stageLabels[activeStage.value?.stage_key] || '正在整理分析资料')
const deliveryLabel = computed(() => reportDeliveryLabel(run.value))
const visibleStageProgress = computed(() => Math.max(0, Math.min(100, Number(activeStage.value?.progress || 0))))
const processedPagesLabel = computed(() => {
  const processed = progressSummary.value.processed_pages
  const limit = progressSummary.value.page_limit
  if (Number.isInteger(processed) && run.value?.stages?.some(stage => stage.stage_key === 'public_web_research' && stage.status === 'succeeded')) {
    return `公开网站已完成 ${processed} 页`
  }
  return Number.isInteger(processed) && Number.isInteger(limit) ? `已处理 ${processed} / 最多 ${limit} 页公开网页` : ''
})

function attentionSourceLabels(item) {
  return (item?.affected_sources || []).map((value) => sourceLabels[value] || '').filter(Boolean).join('、')
}
const reportDetails = reactive({})
const detailStatus = reactive({})
const detailReaders = new Map()
const currentReportId = computed(() => run.value?.stages?.find(item => item.stage_key === 'report_persist')?.checkpoint?.report_id
  || ((!run.value || run.value.status === 'succeeded') ? history.value[0]?.report_id : ''))
const report = computed(() => reportDetails[currentReportId.value]
  || run.value?.stages?.find(item => item.stage_key === 'report_generation')?.checkpoint?.report || null)
const stopReportWatch = watch(currentReportId, id => { if (id) void ensureReport(id) })

function ensureReport(id) {
  if (!id || disposed || reportDetails[id]) return Promise.resolve()
  if (!detailReaders.has(id)) {
    detailStatus[id] = readState()
    detailReaders.set(id, recoveringRead(signal => marketAnalysisApi.report(context.tenantId, context.projectId, id, { signal }),
      value => { reportDetails[id] = value.report }, detailStatus[id]))
  }
  return detailReaders.get(id).run()
}

function detailMessage(id) {
  const state = detailStatus[id]
  if (state?.nextAttemptAt === Infinity) return '报告正文暂时无法读取，请确认当前账号的访问权限。'
  return state?.error ? '报告正文读取暂时中断，正在自动恢复，无需重新分析。' : '正在读取报告正文…'
}

function reportPayload(item) { return reportDetails[item.report_id] || item.report || {} }
function chapterCount(item) { return historicalChapters(item).length || item.report?.chapter_count || 0 }

function historicalChapters(item) {
  const payload = reportPayload(item)
  if (Array.isArray(payload.chapters)) return payload.chapters
  if (Array.isArray(payload.full_report_sections)) return payload.full_report_sections
  return []
}

function historicalSummary(item) {
  const payload = item?.report || {}
  return payload.executive_summary || payload.summary || payload.report_summary || '该报告已从原企业空间读取；正文与原始生成记录保持一致。'
}

function displayTime(value) {
  if (!value) return ''
  return new Intl.DateTimeFormat('zh-CN', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
}

function cloudPdfPath(item) {
  const value = String(item?.pdf_path || '').trim()
  if (value.startsWith('/api/')) return value
  if (value.startsWith(location.origin + '/api/')) return value.slice(location.origin.length)
  return ''
}

async function downloadReport(item) {
  const path = cloudPdfPath(item)
  if (!path || downloadingReportId.value) return
  downloadingReportId.value = String(item.report_id)
  downloadMessage.value = '正在从云端准备 PDF…'
  try {
    await downloadCloudFile(path, { filename: `market-report-v${item.version}.pdf` })
    downloadMessage.value = `报告 v${item.version} 已开始下载，请查看浏览器下载记录。`
  } catch (error) {
    downloadMessage.value = error?.message || 'PDF 暂时无法下载，请稍后重试。'
  } finally {
    downloadingReportId.value = ''
  }
}

function closeStream() { stream?.close(); stream = null }

function refreshHistory() { return readers.history.run() }

const readers = {
  profile: recoveringRead(signal => enterpriseProfileApi.get(context.tenantId, context.projectId, { signal }),
    value => { profile.value = value }, readStatus.profile),
  history: recoveringRead(signal => marketAnalysisApi.history(context.tenantId, context.projectId, { signal, compact: true }),
    value => { history.value = Array.isArray(value) ? value : [] }, readStatus.history),
  run: recoveringRead(async signal => {
    const generation = mutationGeneration
    const latest = await marketAnalysisApi.current(context.tenantId, context.projectId, { signal, compact: true })
    return { latest, generation }
  }, ({ latest, generation }) => {
    if (generation !== mutationGeneration || submission.hasPending()) return
    if (run.value?.id === latest?.id && run.value?.status === 'cancelled' && latest?.status !== 'cancelled') return
    const wasActive = active.value
    const changed = run.value?.id !== latest?.id
    run.value = latest
    if (active.value && (!stream || changed)) subscribe(latest.id)
    if (!active.value) {
      closeStream()
      if (wasActive) void refreshHistory()
    }
  }, readStatus.run),
}

const refreshQueue = coalescedRefresh(() => readers.run.run())
const refreshRun = () => refreshQueue.run()

async function stopAnalysis() {
  if (stopping.value || !active.value) return
  stopping.value = true
  errorMessage.value = ''
  try {
    run.value = await marketAnalysisApi.stop(context.tenantId, context.projectId, run.value.id)
    closeStream()
    statusMessage.value = run.value.status === 'cancelled'
      ? '分析已停止，已完成资料已保留。停止前已发出的请求会记录实际结果。'
      : '分析已完成，当前结果已保留。'
  } catch (error) {
    errorMessage.value = error?.message || '暂时未能确认停止，请重试。'
  } finally { stopping.value = false }
}

function subscribe(runId) {
  closeStream()
  if (!projectRefresh.visible() || disposed) return
  stream = subscribeToCloudEvents({
    path: marketAnalysisApi.eventsPath(context.tenantId, context.projectId, runId),
    onState: ({ state }) => { connectionState.value = state },
    onEvent: (event) => {
      if (event.event === 'stream_complete') closeStream()
      clearTimeout(refreshTimer)
      refreshTimer = setTimeout(() => refreshRun().catch(() => {}), 200)
    },
  })
}

function load() {
  void submission.confirm()
  return Promise.all(Object.values(readers).map(reader => reader.run()))
}
const projectRefresh = useProjectRefresh(projectScope(context.tenantId, context.projectId), load, closeStream)

let lastProgressPoll = 0
function recoverReads() {
  if (disposed || !projectRefresh.visible()) return
  for (const [key, reader] of Object.entries(readers)) {
    if (!readStatus[key].loaded || readStatus[key].error) void reader.run()
  }
  for (const [id, reader] of detailReaders) {
    if (!detailStatus[id].loaded || detailStatus[id].error) void reader.run()
  }
  if (active.value && Date.now() - lastProgressPoll >= 20_000) {
    lastProgressPoll = Date.now()
    void refreshRun()
  }
}

const submission = analysisSubmission({
  storage: (() => { try { return window.sessionStorage } catch { return null } })(),
  storageKey: `analysis-request:${authStore.state.user?.id}:${projectScope(context.tenantId, context.projectId)}`,
  lookup: key => marketAnalysisApi.byRequest(context.tenantId, context.projectId, key),
  create: async body => {
    try {
      return await marketAnalysisApi.create(context.tenantId, context.projectId, body,
        { idempotencyKey: body.idempotency_key, expectedRevision: body.expected_profile_revision, timeoutMs: 30_000 })
    } catch (error) {
      if (error instanceof CloudApiError && error.code === 'MARKET_ANALYSIS_RUN_ALREADY_ACTIVE' && error.details?.run_id) {
        return marketAnalysisApi.get(context.tenantId, context.projectId, error.details.run_id)
      }
      throw error
    }
  },
  onState: phase => {
    if (disposed) return
    submissionPhase.value = phase
    starting.value = ['submitting', 'confirming'].includes(phase)
    statusMessage.value = ({ submitting: '正在提交分析请求，请稍候…', confirming: '正在确认服务器是否已创建分析，请勿重复提交。',
      unconfirmed: '暂时无法确认创建结果。恢复连接后会继续确认；也可使用原请求重试。', accepted: '服务器已接收分析，页面已同步云端进度。' })[phase] || ''
  },
  onRun: value => {
    if (disposed) return
    mutationGeneration++
    run.value = value
    if (active.value) subscribe(value.id)
    else void refreshHistory()
  },
})
async function startAnalysis() {
  if (starting.value) return
  if (profileNeedsCorrection.value && (!correctedVersionReady.value || active.value)) return
  if (!submission.hasPending() && (!readStatus.run.loaded || readStatus.run.error || readStatus.profile.error)) return
  if (!submission.hasPending() && !profile.value?.current) { errorMessage.value = '请先在企业基本资料页保存正式版本。'; return }
  mutationGeneration++
  errorMessage.value = ''
  if (!startKey) startKey = `market-analysis-${profile.value?.current?.id}-${globalThis.crypto.randomUUID()}`
  try {
    await submission.start({ idempotency_key: startKey,
      expected_profile_revision: profile.value?.current?.revision, profile_hash: profile.value?.current?.profile_hash })
  } catch (error) {
    errorMessage.value = error?.message || '分析请求未被接受，请确认资料版本后重试。'
    void load()
  } finally { if (!submission.hasPending()) startKey = '' }
}

async function retryFailedAnalysis() {
  if (starting.value || !run.value?.id || !recovery.value.canResume) return
  const recollectUnsaved = run.value.recovery?.requires_recollection_consent === true
  if (recollectUnsaved && !window.confirm('继续分析会补充上次未保存的研究资料，可能产生重复调用费用；已保存的企业资料和研究结果不会重做。是否继续？')) return
  starting.value = true
  errorMessage.value = ''
  statusMessage.value = 'AI 项目经理正在确认已完成结果并继续未完成部分…'
  const runId = run.value.id
  const pendingLabel = recovery.value.pendingLabel
  try {
    run.value = await marketAnalysisApi.retryFailed(
      context.tenantId,
      context.projectId,
      runId,
      { timeoutMs: 30_000, recollectUnsaved },
    )
    run.value = await marketAnalysisApi.get(context.tenantId, context.projectId, runId)
    if (active.value) {
      subscribe(runId)
      statusMessage.value = pendingLabel || 'AI 项目经理已经继续未完成部分。'
    } else {
      statusMessage.value = ''
      errorMessage.value = '本次继续后仍有步骤未完成。已完成结果已保留，请稍后再试。'
    }
  } catch (error) {
    try {
      run.value = await marketAnalysisApi.get(context.tenantId, context.projectId, runId)
      if (active.value) {
        subscribe(runId)
        statusMessage.value = 'AI 项目经理已经继续未完成部分，页面已同步最新进度。'
      } else {
        statusMessage.value = ''
        errorMessage.value = '当前没有可继续的步骤。已完成结果已保留，请稍后刷新状态。'
      }
    } catch {
      statusMessage.value = ''
      errorMessage.value = error instanceof CloudApiError && error.status === 409
        ? '当前没有可继续的步骤。已完成结果已保留，请稍后刷新状态。'
        : '暂时无法确认恢复状态。已完成结果已保留，请稍后再试。'
    }
  } finally { starting.value = false }
}

onMounted(() => {
  pollTimer = setInterval(recoverReads, 2_000)
})
onBeforeUnmount(() => {
  disposed = true
  stopReportWatch()
  detailReaders.forEach(reader => reader.stop())
  Object.values(readers).forEach(reader => reader.stop())
  refreshQueue.stop()
  closeStream()
  clearInterval(pollTimer)
  clearTimeout(refreshTimer)
})
</script>

<template>
  <section class="analysis-page" aria-labelledby="analysis-title">
    <header class="profile-header">
      <div><p class="eyebrow">AI MARKETING PROJECT MANAGER</p><h1 id="analysis-title">企业市场分析</h1><p>企业资料、公开研究、分析报告与保存均由云端 AI 项目经理持续执行。关闭页面不会中断。</p></div>
      <router-link class="text-button" to="/workspace/brain">返回项目大脑</router-link>
    </header>
    <p v-if="errorMessage" class="form-error" role="alert">{{ errorMessage }}</p>
    <p v-if="statusMessage" class="analysis-guidance" role="status">{{ statusMessage }}</p>
    <button v-if="submissionPhase === 'unconfirmed'" type="button" @click="startAnalysis">使用原请求重试</button>
    <p v-for="message in readMessages" :key="message" class="analysis-guidance" role="status">{{ message }}</p>
    <p v-if="active && ['connecting', 'reconnecting'].includes(connectionState)" class="analysis-guidance" role="status">{{ connectionState === 'reconnecting' ? '进度连接重连中' : '正在连接云端进度' }}，后台任务保留；页面也会每 20 秒查询一次进度。</p>
    <p v-if="active && connectionState === 'auth_required'" class="form-error" role="alert">登录或访问权限已失效，请重新登录后查看进度。</p>
    <div v-if="loading" class="analysis-empty">正在读取云端分析状态…</div>
    <div v-else-if="readStatus.run.loaded && !readStatus.run.error && !run" class="analysis-empty">
      <strong>尚未开始企业分析</strong><p>当前正式资料版本：{{ profile?.current?.revision ? `v${profile.current.revision}` : readStatus.profile.loaded && !readStatus.profile.error ? '尚未保存' : '正在读取' }}</p>
      <button type="button" :disabled="starting || submissionPhase === 'unconfirmed' || !profile?.current || Boolean(readStatus.profile.error)" @click="startAnalysis">{{ starting ? '正在提交分析请求…' : '开始企业分析' }}</button>
    </div>
    <template v-if="run">
      <section v-if="active || profileNeedsCorrection" class="analysis-progress-card" role="status" aria-live="polite">
        <div class="analysis-progress-heading">
          <div><strong>{{ profileNeedsCorrection ? '分析已暂停，需要修正企业资料' : deliveryLabel || activeStageLabel }}{{ !profileNeedsCorrection && !deliveryLabel && progressSummary.is_recovering ? ' · 等待补查' : '' }}</strong><p>{{ processedPagesLabel || runStatusLabel(run.status) }}</p></div>
          <div><time v-if="progressSummary.last_progress_at" :datetime="progressSummary.last_progress_at">最后有效更新：{{ displayTime(progressSummary.last_progress_at) }}</time><p v-if="progressSummary.last_checked_at">最近检查：{{ displayTime(progressSummary.last_checked_at) }}</p></div>
        </div>
        <div class="analysis-progress-track" aria-label="当前阶段进度"><span :style="{ width: `${visibleStageProgress}%` }"></span></div>
        <ol class="analysis-stage-list" aria-label="企业分析阶段">
          <li v-for="stage in run.stages" :key="stage.id" :class="`stage-${stage.status}`">
            <span>{{ stageLabels[stage.stage_key] || stage.stage_key }}</span><strong>{{ stageActivityLabel(stage) }}</strong>
          </li>
        </ol>
        <p v-if="waitingStage" class="analysis-guidance">{{ waitingStage.error_message || '已保存完成部分，系统将自动继续补齐资料。' }}<span v-if="waitingStage.next_attempt_at && waitingStage.error_code !== 'RESEARCH_PROFILE_INVALID'"> 下次检查：{{ displayTime(waitingStage.next_attempt_at) }}。</span></p>
        <p v-if="profileNeedsCorrection && !active" class="analysis-guidance">{{ failureMessage }}</p>
        <button v-if="active" class="secondary-action" type="button" :disabled="stopping" @click="stopAnalysis">{{ stopping ? '正在停止…' : '停止分析' }}</button>
      </section>
      <p v-if="profileNeedsCorrection" class="analysis-guidance" role="alert">{{ active ? '操作顺序：停止旧分析 → ' : '旧分析已停止，已完成资料保留。' }}<router-link :to="{ name: 'project-manager-company-basic', params: { projectId: context.projectId }, hash: '#competitorUrls' }">修正企业资料并保存正式版本</router-link> → 返回本页开始分析。可在网址下方点击“整理竞品网址”，预览并保留说明。</p>
      <button v-if="profileNeedsCorrection && !active" class="secondary-action" type="button" :disabled="!correctedVersionReady || starting || Boolean(readStatus.run.error) || Boolean(readStatus.profile.error)" @click="startAnalysis">{{ starting ? '正在提交分析请求…' : correctedVersionReady ? '使用新正式版本开始分析' : '请先保存新正式版本' }}</button>
      <section v-if="active" v-for="item in attentionItems" :key="item.code" class="analysis-attention" role="status">
        <strong>部分社媒洞察暂时不可用{{ attentionSourceLabels(item) ? `：${attentionSourceLabels(item)}` : '' }}</strong>
        <p>{{ item.message }}</p>
      </section>
      <p v-if="currentReportId && !report" role="status">{{ detailMessage(currentReportId) }}</p>
      <section v-if="report" class="report-card" aria-labelledby="report-title">
        <p role="status">{{ deliveryLabel }}</p>
        <p class="eyebrow">REPORT READY</p><h2 id="report-title">{{ report.report_title || 'AI 企业市场分析报告' }}</h2><p>{{ report.executive_summary }}</p>
        <details v-for="chapter in report.chapters" :key="chapter.no"><summary>第 {{ chapter.no }} 章 · {{ chapter.title }}</summary><ReportChapter :chapter="chapter" /></details>
        <ReportEvidence :report="report" />
      </section>
      <section v-if="run.status === 'failed' && !profileNeedsCorrection" class="analysis-guidance" role="status" aria-live="polite"><strong>{{ recovery.canResume ? '上次分析中断' : waitingForServiceRecovery ? '上次分析已暂存' : 'AI 营销项目经理暂时未完成本次分析' }}</strong><p>{{ recovery.canResume ? '已为你保存到中断的位置。点击继续分析后，AI 项目经理会从这里接着完成。' : waitingForServiceRecovery ? '上次分析已保存，暂时无法继续。服务恢复后，你可以点击“继续分析”，从上次的位置接着完成。' : failureMessage }}</p><p v-if="recovery.canResume">已完成资料会保留，不会重复分析。</p></section>
      <button v-if="recovery.canResume" class="secondary-action" type="button" :disabled="starting" @click="retryFailedAnalysis">{{ starting ? '正在继续分析…' : '继续分析' }}</button>
      <p v-if="run.status === 'cancelled'" role="status">分析已停止，已完成资料已保留。</p>
      <button v-if="!active && !profileNeedsCorrection && submissionPhase !== 'unconfirmed' && run.status !== 'failed'" class="secondary-action" type="button" :disabled="starting || !profile?.current || Boolean(readStatus.run.error) || Boolean(readStatus.profile.error)" @click="startAnalysis">{{ starting ? '正在提交分析请求…' : '重新开始企业分析' }}</button>
    </template>
    <section class="report-history" aria-labelledby="history-title">
      <div class="report-history-heading">
        <div><p class="eyebrow">CLOUD REPORT HISTORY</p><h2 id="history-title">历史企业分析报告</h2></div>
        <strong>{{ readStatus.history.loaded ? `${history.length} 份` : '正在读取' }}</strong>
      </div>
      <p v-if="readStatus.history.loaded && !readStatus.history.error && history.length === 0" class="analysis-empty">当前项目还没有历史报告。旧数据完成回填后会自动显示，无需重新填写企业资料。</p>
      <p v-if="downloadMessage" class="material-state" role="status">{{ downloadMessage }}</p>
      <article v-for="item in history" :key="item.report_id" class="report-card history-report-card">
        <div class="history-report-meta">
          <span>版本 {{ item.version }}</span><span>{{ item.status }}</span><time :datetime="item.created_at">{{ displayTime(item.created_at) }}</time>
        </div>
        <h3>{{ item.title || 'AI 企业市场分析报告' }}</h3>
        <p>{{ historicalSummary(item) }}</p>
        <button v-if="cloudPdfPath(item)" class="text-button" type="button" :disabled="Boolean(downloadingReportId)" @click="downloadReport(item)">
          {{ downloadingReportId === String(item.report_id) ? '正在下载 PDF…' : '下载报告 PDF' }}
        </button>
        <span v-else-if="item.pdf_path" class="history-pdf-unavailable">历史记录未包含可用的云端 PDF</span>
        <details v-if="chapterCount(item)" @toggle="$event.target.open && ensureReport(item.report_id)">
          <summary>查看报告章节（{{ chapterCount(item) }}）</summary>
          <p v-if="!historicalChapters(item).length" role="status">{{ detailMessage(item.report_id) }}</p>
          <ol class="history-chapters">
            <li v-for="(chapter, index) in historicalChapters(item)" :key="chapter.no || chapter.title || index">
              <strong>{{ chapter.no || String(index + 1).padStart(2, '0') }} · {{ chapter.title || '报告章节' }}</strong>
              <ReportChapter :chapter="chapter" />
            </li>
          </ol>
        </details>
        <ReportEvidence v-if="reportDetails[item.report_id]" :report="reportDetails[item.report_id]" />
      </article>
    </section>
  </section>
</template>

<style scoped>
.report-history,.history-chapters{grid-template-columns:minmax(0,1fr)}
.report-card,.history-chapters>li{min-width:0;overflow-wrap:anywhere}
.analysis-progress-card,.analysis-attention{display:grid;gap:12px;margin:16px 0;padding:18px;border:1px solid #bfe2d9;border-radius:14px;background:#f4fbf8}
.analysis-progress-heading{display:flex;justify-content:space-between;gap:16px;align-items:start}.analysis-progress-heading p{margin:5px 0 0;color:#49656c}.analysis-progress-heading time{color:#64748b;font-size:13px;white-space:nowrap}.analysis-progress-track{height:9px;overflow:hidden;border-radius:99px;background:#dcefe9}.analysis-progress-track span{display:block;height:100%;border-radius:inherit;background:#14806c;transition:width .25s ease}.analysis-stage-list{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;margin:0;padding:0;list-style:none}.analysis-stage-list li{display:flex;justify-content:space-between;gap:8px;padding:9px;border-radius:9px;background:#fff;color:#51616a;font-size:13px}.analysis-stage-list strong{white-space:nowrap}.stage-succeeded strong{color:#087c5c}.stage-running,.stage-waiting_retry{border:1px solid #8ccdbd}.stage-failed strong{color:#9b5f17}.analysis-attention{border-color:#e6d1a7;background:#fffaf0}.analysis-attention p{margin:0;color:#635841}
@media(max-width:600px){.analysis-page{padding:16px}.report-card{padding:14px}.history-chapters{padding-left:18px}}
@media(max-width:760px){.analysis-progress-heading{display:grid}.analysis-progress-heading time{white-space:normal}.analysis-stage-list{grid-template-columns:1fr}.analysis-stage-list li{align-items:start;flex-direction:column}}
</style>
